import { FC, useEffect, useMemo, useState } from "react";
import { ConnectProto, ErrorCode, type ApiError } from "@racoon/api";
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@racoon/ui";
import { IcCancel } from "@racoon/icons";
import { format, parseISO } from "date-fns";
import { Namespace, TranslationKey, useApiErrorMessage, useT } from "@racoon/i18n";
import { parseLocalDateParam, toDateLike, toDateParam } from "@racoon/lib";
import {
  useCreateContainerGroupScheduleMutation,
  useUpdateContainerGroupScheduleMutation,
} from "~/shared/api/container-group-schedule/container-group-schedule.grpc";
import { useZonesEnabled } from "~/shared/hooks/use-zones-enabled";
import {
  emptyScheduleForm,
  formToCreate,
  formToSave,
  formToUpdate,
  scheduleToForm,
  isSimplified,
  simplifyForm,
  withStoredRules,
  validateScheduleForm,
} from "../lib/schedule-form";
import { contractJobTypes } from "../lib/contract-job-types";
import { containerGroupName } from "../lib/schedule-label";
import type { RuleKind, ScheduleForm } from "../model/types";
import { useSchedulePreview } from "../model/use-schedule-preview";
import { RulesEditor } from "./rules-editor";

/** Radix Select has no empty value; this one stands for "the zone's default". */
const DEFAULT_PROVIDER = "default";

/** Mounted while open, so every opening starts from the schedule as stored. */
type Props = {
  onClose: () => void;
  containerGroups: ConnectProto.ContainerGroup[];
  /** The schedule being edited; none creates one. */
  schedule?: ConnectProto.ContainerGroupSchedule;
  /** One-line names of the location's schedules, by id, for conflicts. */
  scheduleNames: Map<string, string>;
  /** The schedule's place in the location's list, from 1, for the title. */
  number?: number;
};

const KINDS: RuleKind[] = ["weekly", "monthly", "everyNDays"];

const toDay = (date?: Date) => (date ? toDateParam(date) : "");

export const ScheduleEditorModal: FC<Props> = ({
  onClose,
  containerGroups,
  schedule,
  scheduleNames,
  number,
}) => {
  const t = useT();
  const { toast } = useToast();
  const describeError = useApiErrorMessage();
  const editingId = schedule ? String(schedule.id) : undefined;

  // The editor shows one window a day and no month filter; a richer stored schedule is flagged.
  const [stored] = useState(() => (schedule ? scheduleToForm(schedule) : undefined));
  const [form, setForm] = useState<ScheduleForm>(() =>
    stored
      ? simplifyForm(stored)
      : emptyScheduleForm(
          containerGroups[0] ? String(containerGroups[0].id) : "",
          toDateParam(new Date()),
        ),
  );
  const simplified = !!stored && isSimplified(stored);
  // Saved and previewed: the stored rules unless the user changed the shown ones.
  const saved = useMemo(() => withStoredRules(form, stored), [form, stored]);
  const [attempted, setAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<ApiError>();
  const patch = (next: Partial<ScheduleForm>) => {
    setForm((prev) => ({ ...prev, ...next }));
    setSubmitError(undefined);
  };

  const cg = containerGroups.find((g) => String(g.id) === form.containerGroupId);
  const jobTypes = useMemo(() => contractJobTypes(cg), [cg]);
  const jobTypeOptions = schedule ? [form.jobType] : jobTypes;

  // A new schedule follows the group's contract: the first priced job type, none when unpriced.
  useEffect(() => {
    if (schedule || jobTypes.includes(form.jobType)) return;
    setForm((prev) => ({ ...prev, jobType: jobTypes[0] ?? "" }));
  }, [schedule, jobTypes, form.jobType]);

  // The group's zone, plus the stored provider should it have left the zone since.
  const providers = useMemo(() => {
    const zone = cg?.serviceProviders ?? [];
    const stored = schedule?.serviceProvider;
    return stored && !zone.some((o) => String(o.id) === String(stored.id))
      ? [...zone, stored]
      : zone;
  }, [cg, schedule]);
  // The stored provider's organization is gone: the backend no longer returns it.
  const deletedProviderId =
    schedule?.serviceProviderId && !schedule.serviceProvider
      ? String(schedule.serviceProviderId)
      : undefined;
  // With zones off the group has no depots to pick: only a stored one keeps the select.
  const showProvider = useZonesEnabled() || providers.length > 0 || !!deletedProviderId;

  const { preview, error: previewError } = useSchedulePreview(saved, editingId);

  const [createSchedule, { isLoading: creating }] = useCreateContainerGroupScheduleMutation();
  const [updateSchedule, { isLoading: updating }] = useUpdateContainerGroupScheduleMutation();

  const formError = validateScheduleForm(form);

  // An overlap names the other schedule by its id; show its name instead.
  const errorText = (error: ApiError) =>
    describeError(
      error.code === ErrorCode.ERROR_CODE_CONTAINER_GROUP_SCHEDULE_OVERLAP
        ? {
            ...error,
            args: {
              ...error.args,
              schedule: scheduleNames.get(error.args.schedule) ?? error.args.schedule,
            },
          }
        : error,
    );
  const shownError = submitError ?? (previewError as ApiError | undefined);
  const conflict = preview?.conflicts[0];

  const contract = cg?.contract;
  const contractEnd = toDateLike(contract?.endAt);

  const handleSave = async () => {
    setAttempted(true);
    if (formError) return;
    try {
      const sent = formToSave(saved, showProvider);
      if (schedule) await updateSchedule(formToUpdate(schedule, sent)).unwrap();
      else await createSchedule(formToCreate(sent)).unwrap();
      toast({ title: t(Namespace.PickupLocation, "schedules.saved") });
      onClose();
    } catch (error) {
      setSubmitError(error as ApiError);
    }
  };

  const labelClass = "whitespace-nowrap text-sm font-medium text-text-body";
  const hintClass = "text-xs text-text-subtle";

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] w-[1400px] gap-0 overflow-hidden rounded-2xl border-border-primary !p-0">
        <DialogHeader className="border-b border-border-primary !p-4">
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-text-body">
            {t(
              Namespace.PickupLocation,
              schedule ? "schedules.editor.editTitle" : "schedules.editor.createTitle",
            )}
            {number && (
              <span className="text-text-information">
                {t(Namespace.PickupLocation, "schedules.editor.number", { n: number })}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex max-h-[calc(90vh-130px)] flex-col gap-8 overflow-y-auto p-4">
          <div className="flex items-end justify-between gap-8">
            <div className="flex w-[800px] min-w-0 flex-col gap-4">
              <div className="flex items-center gap-2.5">
                <Select
                  value={form.containerGroupId}
                  onValueChange={(containerGroupId) =>
                    patch({ containerGroupId, serviceProviderId: "" })
                  }
                  disabled={!!schedule}
                >
                  <SelectTrigger
                    aria-label={t(Namespace.PickupLocation, "schedules.editor.containerGroup")}
                    className="!h-8 min-w-0 flex-1 !px-2 text-sm"
                  >
                    <span className="flex-1 truncate text-left">{containerGroupName(cg, t)}</span>
                    <span className="ml-2 truncate text-xs font-medium text-text-subtle">
                      {cg?.ownerOrganization?.name}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {containerGroups.map((group) => (
                      <SelectItem key={String(group.id)} value={String(group.id)}>
                        {containerGroupName(group, t)}
                        {group.ownerOrganization?.name && ` · ${group.ownerOrganization.name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={form.jobType}
                  onValueChange={(jobType) => patch({ jobType })}
                  disabled={!!schedule || jobTypeOptions.length === 0}
                >
                  <SelectTrigger
                    aria-label={t(Namespace.PickupLocation, "schedules.editor.jobType")}
                    className="!h-8 !w-[200px] shrink-0 !px-2 text-sm"
                  >
                    <SelectValue
                      placeholder={t(Namespace.PickupLocation, "schedules.editor.noJobTypes")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {jobTypeOptions.map((jobType) => (
                      <SelectItem key={jobType} value={jobType}>
                        {t(Namespace.Enum, jobType as TranslationKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {showProvider && (
                  <Select
                    value={form.serviceProviderId || DEFAULT_PROVIDER}
                    onValueChange={(id) =>
                      patch({ serviceProviderId: id === DEFAULT_PROVIDER ? "" : id })
                    }
                    disabled={providers.length === 0 && !deletedProviderId}
                  >
                    <SelectTrigger
                      aria-label={t(Namespace.PickupLocation, "schedules.editor.serviceProvider")}
                      className="!h-8 !w-[200px] shrink-0 !px-2 text-sm"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DEFAULT_PROVIDER}>
                        {t(Namespace.PickupLocation, "schedules.defaultProvider")}
                      </SelectItem>
                      {providers.map((org) => (
                        <SelectItem key={String(org.id)} value={String(org.id)}>
                          {org.name}
                        </SelectItem>
                      ))}
                      {deletedProviderId && (
                        <SelectItem value={deletedProviderId}>
                          {t(Namespace.PickupLocation, "schedules.deletedProvider", {
                            id: deletedProviderId,
                          })}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {deletedProviderId && form.serviceProviderId === deletedProviderId ? (
                <span className="text-xs text-text-error">
                  {t(Namespace.PickupLocation, "schedules.editor.deletedProviderHint")}{" "}
                  <button
                    type="button"
                    className="text-text-information underline"
                    onClick={() => patch({ serviceProviderId: "" })}
                  >
                    {t(Namespace.PickupLocation, "schedules.editor.clearProvider")}
                  </button>
                </span>
              ) : (
                showProvider &&
                providers.length === 0 && (
                  <span className={hintClass}>
                    {t(Namespace.PickupLocation, "schedules.editor.noZoneHint")}
                  </span>
                )
              )}

              {contract && (
                <span className="w-fit rounded-md bg-surface-muted p-2 text-sm text-text-body">
                  {[
                    contract.name || `№${contract.dealNumber}`,
                    contractEnd &&
                      t(Namespace.PickupLocation, "schedules.editor.contractUntil", {
                        date: format(contractEnd, "dd.MM.yyyy"),
                      }),
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </span>
              )}
            </div>

            <div className="flex shrink-0 flex-col gap-4">
              <div className="flex items-center justify-end gap-8">
                <span className={labelClass}>
                  {t(Namespace.PickupLocation, "schedules.editor.validFrom")}
                </span>
                <div className="flex items-center gap-4">
                  <DatePicker
                    value={parseLocalDateParam(form.validFrom)}
                    onChange={(date) => patch({ validFrom: toDay(date) })}
                    className="!h-8 !px-3 text-xs font-medium text-text-body"
                  />
                  <span className={labelClass}>
                    {t(Namespace.PickupLocation, "schedules.editor.validTo")}
                  </span>
                  <DatePicker
                    value={parseLocalDateParam(form.validTo)}
                    onChange={(date) => patch({ validTo: toDay(date) })}
                    minDate={parseLocalDateParam(form.validFrom)}
                    placeholder={t(Namespace.PickupLocation, "schedules.openEnded")}
                    className="!h-8 !px-3 text-xs font-medium text-text-body"
                  />
                  {form.validTo && (
                    <IconButton
                      size="sm"
                      aria-label={t(Namespace.PickupLocation, "schedules.openEnded")}
                      onClick={() => patch({ validTo: "" })}
                    >
                      <IcCancel className="text-icon-secondary" />
                    </IconButton>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-8">
                <span className={labelClass}>
                  {t(Namespace.PickupLocation, "schedules.editor.kind")}
                </span>
                <Select
                  value={form.kind}
                  onValueChange={(kind) => patch({ kind: kind as RuleKind })}
                >
                  <SelectTrigger
                    aria-label={t(Namespace.PickupLocation, "schedules.editor.kind")}
                    className="!h-8 min-w-0 flex-1 !px-2 text-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KINDS.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {t(Namespace.PickupLocation, `schedules.editor.kinds.${kind}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {(simplified || schedule?.legacyExpr) && (
            <div className="flex flex-col gap-1">
              {simplified && (
                <span className="text-sm text-text-warning">
                  {t(Namespace.PickupLocation, "schedules.editor.simplified")}
                </span>
              )}
              {schedule?.legacyExpr && (
                <span className={hintClass}>
                  {t(Namespace.PickupLocation, "schedules.editor.legacyExpr")}:{" "}
                  <code>{schedule.legacyExpr}</code>
                </span>
              )}
            </div>
          )}

          <RulesEditor form={form} onChange={patch} preview={preview} />
        </div>

        <div className="flex items-center justify-end gap-4 border-t border-border-primary p-4">
          {attempted && formError ? (
            <span className="text-sm text-text-error">
              {t(Namespace.PickupLocation, `schedules.errors.${formError}`)}
            </span>
          ) : shownError ? (
            <span className="text-sm text-text-error">{errorText(shownError)}</span>
          ) : (
            conflict && (
              // Save checks overlaps further ahead than the preview reaches.
              <span className="text-sm text-text-error">
                {t(Namespace.PickupLocation, "schedules.editor.conflict", {
                  date: format(parseISO(conflict.date), "dd.MM.yyyy"),
                  schedule:
                    scheduleNames.get(String(conflict.scheduleId)) ?? `#${conflict.scheduleId}`,
                })}
                {preview.conflicts.length > 1 &&
                  ` ${t(Namespace.PickupLocation, "schedules.editor.conflictMore", {
                    count: preview.conflicts.length - 1,
                  })}`}
              </span>
            )
          )}
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={creating || updating}
            className="bg-surface-action"
          >
            {t(Namespace.PickupLocation, "schedules.editor.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
